import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Flex, Icon } from '@kognitos/lattice';
import {
  useGetGuideEntry,
  getGetGuideEntryQueryKey,
} from '@/generated/hooks/astral/v1/api';
import { parseResourceName } from '@/shared/utils/api-utils';
import { getRoutePath } from '@/routes/util';
import type { IParsedGuideEntry } from '@/shared/utils/chat-utils';
import { EGuideEntryState } from './InlineGuideEntryWidget';

/**
 * Props for the GuideEntryApprovalPills component.
 */
interface IGuideEntryApprovalPillsProps {
  /** Parsed guide entry the pills act on */
  guideEntry: IParsedGuideEntry;
  /** Approves the entry into the resolution playbook (pending → approved) */
  onApprove?: (guideEntry: IParsedGuideEntry) => Promise<void> | void;
  /** Rejects the entry (pending → rejected) */
  onReject?: (guideEntry: IParsedGuideEntry) => Promise<void> | void;
  /**
   * Optional "Edit in chat" action shown alongside the pending pills (used by the
   * rain flow to focus the composer). Omitted in the astral flow.
   */
  onEdit?: () => void;
  /** Whether a transition is already in progress (disables the pills) */
  isLoading?: boolean;
}

/**
 * Approve/reject pills rendered below an {@link InlineGuideEntryWidget} in the
 * run-assistant chat.
 *
 * State-dependent:
 * - Pending → "Add to Resolution Playbook" (approve) + "Reject".
 * - Approved → "Added to Resolution Playbook ↗" linking to the entry details.
 * - Rejected/archived → nothing.
 *
 * Fetches the entry so the pills reflect the live state after a transition
 * (shares the same query key as the widget, so the fetch is deduped).
 */
function GuideEntryApprovalPills({
  guideEntry,
  onApprove,
  onReject,
  onEdit,
  isLoading = false,
}: IGuideEntryApprovalPillsProps) {
  const { t } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const {
    organization,
    workspace,
    automation,
    guideEntry: guideEntryId,
  } = useMemo(() => {
    const parsed = parseResourceName(guideEntry.name);
    return {
      organization: parsed.organization || '',
      workspace: parsed.workspace || '',
      automation: parsed.automation || '',
      guideEntry: parsed.guideEntry || '',
    };
  }, [guideEntry.name]);

  const { data: guideEntryResponse } = useGetGuideEntry(
    organization,
    workspace,
    automation,
    guideEntryId,
    {
      query: {
        enabled: !!(organization && workspace && automation && guideEntryId),
        staleTime: 0,
        refetchOnMount: true,
      },
    },
  );

  const state = guideEntryResponse?.data?.state || guideEntry.state;
  const isApproved = state === EGuideEntryState.APPROVED;
  const isPending =
    state === EGuideEntryState.PENDING_APPROVAL ||
    state === EGuideEntryState.UNSPECIFIED ||
    !state;

  const refreshGuideEntry = async () => {
    await queryClient.invalidateQueries({
      queryKey: getGetGuideEntryQueryKey(
        organization,
        workspace,
        automation,
        guideEntryId,
      ),
    });
  };

  const runTransition = async (
    action?: (guideEntry: IParsedGuideEntry) => Promise<void> | void,
  ) => {
    if (!action) return;
    setIsActionInProgress(true);
    try {
      await action(guideEntry);
      await refreshGuideEntry();
    } catch {
      // Toast + Sentry handled by the transition hook; don't refetch on error.
    } finally {
      setIsActionInProgress(false);
    }
  };

  const isDisabled = isLoading || isActionInProgress;

  if (isApproved) {
    const entryUrl = getRoutePath('RESOLUTION_PLAYBOOK_ENTRY_DETAILS', {
      orgId: organization,
      workspaceId: workspace,
      automationId: automation,
      entryId: guideEntryId,
    });
    return (
      <Button asChild variant="secondary" size="sm">
        <Link to={entryUrl} target="_blank" rel="noopener noreferrer">
          {t('automation.runAssistant.addedToResolutionPlaybook')}
          <Icon type="ArrowUpRight" size="sm" />
        </Link>
      </Button>
    );
  }

  if (!isPending) return null;

  return (
    <Flex gap={2}>
      <Button
        variant="default"
        size="sm"
        onClick={() => runTransition(onApprove)}
        disabled={isDisabled}
      >
        {t('automation.runAssistant.addToResolutionPlaybook')}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => runTransition(onReject)}
        disabled={isDisabled}
      >
        {tCommon('common.action.reject')}
      </Button>
      {onEdit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          disabled={isDisabled}
        >
          {tCommon('common.action.edit')}
        </Button>
      )}
    </Flex>
  );
}

export default GuideEntryApprovalPills;
